// allow reading and upating GDPR info global, even if mp.globals does not exist on the page
// this lets this code run even in non-mixpanel.com environments like the marketing site

export class GdprGlobals {
  constructor() {
    this._data = {};
    this._onUpdateCallbacks = [];
    this._updateCallbackTimeoutID = null;
  }

  get _globals() {
    return (window.mp && window.mp.globals) || {};
  }

  get _gdprData() {
    return this._globals.gdpr || this._data;
  }

  get _settingUp2fa() {
    return this._globals.setting_up_2fa;
  }

  update(gdprData) {
    const previousData = this.getAllData();
    Object.assign(this._gdprData, gdprData);

    // for waitUntilReady()
    if (this.isReady) {
      if (this._readyCallback) {
        this._readyCallback();
        delete this._readyPromise;
        delete this._readyCallback;
      }

      const updatedData = this.getAllData();
      const hasChanges = Object.keys(updatedData).find((key) => previousData[key] !== updatedData[key]);
      if (hasChanges) {
        // A simplified debounce to prevent spamming callbacks
        const debounceMS = 500;
        clearTimeout(this._updateCallbackTimeoutID);
        this._updateCallbackTimeoutID = setTimeout(
          () => this._onUpdateCallbacks.forEach((func) => func(updatedData)),
          debounceMS,
        );
      }
    }
  }

  registerOnUpdateCallback(callback) {
    this._onUpdateCallbacks.push(callback);
  }

  waitUntilReady() {
    if (this.isReady) {
      return Promise.resolve();
    }

    if (!this._readyPromise) {
      this._readyPromise = new Promise((resolve) => {
        this._readyCallback = resolve;
      });
    }

    return this._readyPromise;
  }

  get isUserLoggedIn() {
    return Number.isInteger(parseInt(this._globals[`user_id`], 10));
  }

  // has is_gdpr state been retrieved from the server yet? (either via mp.globals or ajax request)
  get isReady() {
    return Object.prototype.hasOwnProperty.call(this._gdprData, `is_gdpr`);
  }

  get isGdpr() {
    if (!this.isReady) {
      throw new Error(`Cannot check gdpr.isGdpr before GDPR data is initialized.`);
    }
    return !!this._gdprData[`is_gdpr`];
  }

  get optTracking() {
    return this._gdprData[`opt_tracking`];
  }

  get optCookies() {
    return this._gdprData[`opt_cookies`];
  }

  get allowTracking() {
    return (
      // if GDPR doesn't apply, allow tracking if user hasn't opted out
      // if GDPR applies, only allow tracking if user has explicitly opted in
      (!this.isGdpr && this.optTracking !== `out`) || (this.isGdpr && this.optTracking === `in`)
    );
  }

  get allowStorage() {
    return (
      // if GDPR doesn't apply, allow cookies if user hasn't opted out
      // if GDPR applies, only allow cookies if user has explicitly opted in
      (!this.isGdpr && this.optCookies !== `out`) || (this.isGdpr && this.optCookies === `in`)
    );
  }

  get allowSnippets() {
    return this.allowTracking && this.allowStorage;
  }

  get showBanner() {
    return this.isGdpr && ![`in`, `out`].includes(this.optTracking) && ![`in`, `out`].includes(this.optCookies);
  }

  /* Serialize important information to a single object
   * Google Tag Manager creates a sandboxed environment
   * This helper provides a path to ask for all relevant information
   */
  getAllData() {
    let gdprData = {};
    if (this.isReady) {
      gdprData = {
        isGdpr: this.isGdpr,
        showBanner: this.showBanner,
        allowTracking: this.allowTracking,
        allowStorage: this.allowStorage,
      };
    }
    return gdprData;
  }
}

export const gdprGlobals = new GdprGlobals();

// gdpr tests really really want this to be a default export
// eslint-disable-next-line import/no-default-export
export default gdprGlobals;
